import React, {
  memo,
  ForwardedRef,
  useImperativeHandle,
  useRef,
  useMemo,
} from 'react';
import Chart from '../../../../../components/Chart';
import { ChartRef } from '../../../../../components/Chart/_config/interface';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import getChartTooltip from './tooltip';
import { HorizontalBarChartProps } from '../type';

/**
 * HorizontalBarChartInner 组件用于渲染横向柱状图。
 * 
 * @param {Object} props - 组件的属性。
 * @param {string} [props.className=''] - 自定义的 CSS 类名。
 * @param {Object} [props.style={}] - 自定义的样式对象。
 * @param {number} [props.height] - 图表的高度。
 * @param {boolean} [props.hasDataZoom] - 是否启用数据缩放。
 * @param {Object} [props.chartEventConfig] - 图表事件配置。
 * @param {boolean} [props.notMerge] - 是否不合并选项。
 * @param {Object} restProps - 其他传递给图表的属性。
 * @param {Function} [restProps.setOtherConfigFunc] - 用于设置其他配置的函数。
 * @param {Function} [restProps.formatterFunc] - 自定义格式化函数。
 * @param {boolean} [restProps.isShowLegend=true] - 是否显示图例。
 * @param {Array} [restProps.legendData=[]] - 图例数据。
 * @param {Array} [restProps.yData=[]] - Y轴类目数据。
 * @param {Array} [restProps.series] - 图表系列数据。
 * @param {string} [restProps.xLabelFormat='intBmk00'] - X轴标签格式化规则。
 * @param {boolean} [restProps.isEn] - 是否为英文环境。
 * @param {boolean} [restProps.isCategoryEn] - 类目是否为英文。
 * @param {number} [restProps.maxCategoryWidth] - 类目最大宽度。
 * @param {Function} [restProps.tooltipParamsFormat] - 提示框参数格式化函数。
 * 
 * @returns {JSX.Element} 渲染的横向柱状图组件。
 */
const HorizontalBarChartInner: React.ForwardRefRenderFunction<ChartRef, HorizontalBarChartProps> = (props, ref: ForwardedRef<ChartRef>) => {
  const {
    className = '',
    style = {},
    height,
    hasDataZoom,
    chartEventConfig,
    notMerge,
    getChart,
    ...restProps
  } = props;
  const chartRef = useRef<ChartRef>(null);

  const option = useMemo(() => {
    const { 
      setOtherConfigFunc, 
      isShowLegend = true, 
      legendData = [], 
      yData = [],
      series = [],
      xLabelFormat = 'intBmk00',
      isEn = false,
      isCategoryEn,
      maxCategoryWidth,
      tooltipParamsFormat,
      tooltipConfig,
    } = restProps || {};

    // 计算Y轴标签所需的最大宽度
    const getMaxWidth = (arr: string[], isEn?: boolean, maxCategoryWidth?: number) => {
      if (!Array.isArray(arr) || arr.length === 0) {
        return 65;
      }
      const maxLength = arr.reduce(
        (maxLength, str) => Math.max(maxLength, str.length),
        0,
      );
      let max = isCategoryEn ?? isEn ? maxLength * 6 : maxLength * 10;
      if (maxCategoryWidth) {
        max = max > maxCategoryWidth ? maxCategoryWidth : max;
      }
      return max;
    };

    const maxYWidth = getMaxWidth(yData, isEn, maxCategoryWidth);

    // 处理系列数据，设置为横向柱状图
    const newSeries = series?.map((item) => ({
      type: 'bar',
      stack: 'Total',
      barMaxWidth: 30,
      label: {
        show: false,
      },
      emphasis: {
        disable: true,
      },
      ...item,
    }));

    const option = {
      grid: {
        top: 10,
        left: maxYWidth - 40,
        right: 30,
        bottom: 30,
        containLabel: true
      },
      color: ['#4F86F3', '#69D19C', '#69738E', '#F8D74D', '#7F6FF1', '#5DC8F1'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        confine: true,
        padding: 0,
        borderWidth: 0,
        formatter(params: any) {
          const newParams = tooltipParamsFormat?.(params) || params;
          return getChartTooltip({ params: newParams, tooltipConfig, isEn });
        },
      },
      legend: {
        show: isShowLegend,
        type: 'scroll',
        data: legendData,
        selectedMode: true,
        orient: 'horizontal',
        bottom: 0,
        itemWidth: 8,
        itemHeight: 8,
        icon: 'circle',
        itemGap: 18,
        left: 'center',
        textStyle: {
          fontSize: 12,
          color: '#7C7C7C',
        },
      },
      xAxis: {
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { 
            color: '#e5e5e5', 
            type: 'dashed' 
          },
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          fontSize: 12,
          color: '#838697',
          formatter: (value: number, index: number) => {
            // 间隔1个坐标展示，避免数值重叠
            if (index % 2 === 0) {
              const newValue = dataFormat({
                value,
                formatRule: xLabelFormat,
              });
              return newValue;
            }
            return '';
          },
        },
      },
      yAxis: [
        {
          type: 'category',
          position: 'left',
          splitLine: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#e5e5e5',
            },
          },
          axisTick: {
            show: false,
          },
          data: yData || [],
          axisLabel: {
            interval: 0,
            show: true,
            fontWeight: 400,
            fontSize: 12,
            color: '#7C7C7C',
            align: 'left',
            rich: {
              bold: {
                fontWeight: '600',
                color: '#FFBD57',
                fontSize: 12,
              },
            },
          },
          offset: 110,
        },
      ],
      series: newSeries || [],
    };

    return setOtherConfigFunc ? setOtherConfigFunc?.(option) : option;
  }, [restProps]);

  // 刷新图表
  const refreshChart = () => {
    instance()?.setOption(option);
  };

  // 获取实例
  const instance = () => chartRef?.current?.instance();

  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance,
    refreshChart
  }));

  // 根据数据动态计算高度
  const { yData = [] } = restProps || {};
  const calculatedHeight = yData?.length * 30 + 40;
  const finalHeight = height || (calculatedHeight < 300 ? 300 : calculatedHeight);

  const chartStyle = {
    height: `${finalHeight}px`,
    width: '100%',
    position: 'relative' as const,
    ...style,
  };

  return (
    <div className={`chart-box horizontal-bar-chart ${className}`} style={chartStyle}>
      <Chart
        ref={chartRef}
        option={option}
        hasDataZoom={hasDataZoom}
        chartEventConfig={chartEventConfig}
        notMerge={notMerge}
        getChart={getChart}
      />
    </div>
  );
};

const HorizontalBarChart = memo(React.forwardRef(HorizontalBarChartInner));

export default HorizontalBarChart;